/**
 * LoginPage — magic-link sign-in (route `/login`).
 *
 * Story: epic-uat-portal-001-8.
 *
 * An existing tester enters their email; the backend sends a sign-in link
 * (`/request-link` → 204 ALWAYS, no account enumeration). The screen then shows a
 * "check your email" state with a resend control, so a tester whose email is slow
 * or undelivered is never left hanging (plan Review Focus item: magic-link
 * slow-email UX).
 *
 * The actual link lands on `/verify?token=…` (VerifyPage). `/login` only kicks
 * off the send. An optional `returnUrl` query param (set by ProtectedRoute) is
 * preserved on the resend state for display/debugging; VerifyPage honours it.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  requestLink,
  TesterAccountApiError,
} from '@/services/testerAccountService';
import { getRememberedEmail, setRememberedEmail } from '@/auth/rememberedEmail';

export function LoginPage() {
  const [email, setEmail] = useState(() => getRememberedEmail() ?? '');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function send(targetEmail: string) {
    setError(null);
    setSubmitting(true);
    try {
      await requestLink(targetEmail);
      setRememberedEmail(targetEmail);
      setSent(true);
    } catch (err) {
      const message =
        err instanceof TesterAccountApiError
          ? err.message
          : 'Could not send the sign-in link. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await send(email.trim());
  }

  if (sent) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="w-full max-w-sm">
          <h1 className="mb-2 text-2xl font-bold" data-testid="login-sent-heading">
            Check your email
          </h1>
          <p className="mb-6 text-sm text-muted-foreground" data-testid="login-sent-message">
            If <span className="font-medium">{email}</span> has a tester account,
            a sign-in link is on its way. Open it on this device to continue.
          </p>

          {error && (
            <p role="alert" data-testid="login-error" className="mb-3 text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="button"
            disabled={submitting}
            onClick={() => void send(email.trim())}
            data-testid="login-resend"
            className="rounded border border-gray-300 px-4 py-2 font-medium disabled:opacity-60"
          >
            {submitting ? 'Resending…' : 'Resend link'}
          </button>

        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 p-8">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-2xl font-bold" data-testid="login-heading">
          Tester sign-in
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Enter your email and we&apos;ll send you a one-time sign-in link.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="rounded border border-gray-300 px-3 py-2"
              autoComplete="email"
            />
          </label>

          {error && (
            <p role="alert" data-testid="login-error" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="rounded bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-60"
          >
            {submitting ? 'Sending…' : 'Send sign-in link'}
          </button>
        </form>

        <p className="mt-6 text-xs text-muted-foreground">
          Have an invite? <Link to="/redeem" className="underline">Redeem it here.</Link>
        </p>
      </div>
    </main>
  );
}
